import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from '@mui/material';
import { getBusinessBankAccounts } from 'lib/api/bankAccounts';
import BankAccountDialog from 'lib/common/BankAccountDialog';
import { BankAccount, PayoutDistribution } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import EditPayoutPlanDialog from './components/EditPayoutPlanDialog';
import NotAvailableBankAccountsTitle from './components/notAvailableBankAccountsTitle';
import PayoutPlanDetails from './components/PayoutPlanDetails';
import PayoutPlanSkeleton from './components/PayoutPlanSkeleton';
import PayoutPlanTitle from './components/PayoutPlanTitle';

interface PayoutPlanPanelProps {
  businessIdentifier: string;
  operationToDeposit: number | null;
  expanded: boolean;
  payoutPlan: PayoutDistribution[];
  onEditing: boolean;
  setPayoutPlan: (plan: PayoutDistribution[]) => void;
  onAccordionChange: (
    panel: string,
  ) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
}

type PayoutPlanState = 'noBankAccount' | 'settled';

const PayoutPlanPanel: React.FC<PayoutPlanPanelProps> = ({
  operationToDeposit,
  expanded,
  businessIdentifier,
  onAccordionChange,
  payoutPlan,
  setPayoutPlan,
  onEditing,
}) => {
  const dispatch = useDispatch();
  const payoutPanelAccordionChange = onAccordionChange('panel3');
  const [selectableBankAccounts, setSelectableBankAccounts] = useState<
    BankAccount[]
  >([]);

  const [openCreateBankAccount, setOpenCreateBankAccount] = useState(false);
  const [openEditPayoutPlan, setOpenEditPayoutPlan] = useState(false);

  const [actualState, setActualState] =
    useState<PayoutPlanState>('noBankAccount');
  const [loading, setLoading] = useState(true);

  function getAccumulatedAmount() {
    return payoutPlan.reduce((acc, p) => (acc += p.amount), 0);
  }

  function canShowPayoutPlanTitle() {
    return actualState === 'settled' && operationToDeposit !== null;
  }

  function canShowPayoutPlanDetails() {
    return (
      actualState === 'settled' &&
      operationToDeposit !== null &&
      operationToDeposit > 0
    );
  }

  function handleAccordionChange(
    event: React.SyntheticEvent,
    isExpanded: boolean,
  ) {
    if (actualState === 'noBankAccount') return;
    payoutPanelAccordionChange(event, isExpanded);
  }

  function handleSuccessCreateBankAccount() {
    setLoading(true);
    void fetchBankAccounts();
  }

  async function setUp() {
    setLoading(true);
    await fetchBankAccounts();
  }

  async function fetchBankAccounts() {
    const response = await getBusinessBankAccounts(
      dispatch,
      businessIdentifier,
    );
    setSelectableBankAccounts(
      response.map((b) => ({ ...b, type: b.accountType })),
    );
    if (response.length !== 0) setActualState('settled');
    else setActualState('noBankAccount');

    setLoading(false);
    return response;
  }

  useEffect(() => {
    void setUp();
  }, [actualState]);

  if (loading)
    return (
      <Box
        sx={{
          minHeight: 'auto',
          padding: '16px 20px',
          backgroundColor: 'white',
        }}
      >
        <PayoutPlanSkeleton />
      </Box>
    );

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={handleAccordionChange}
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '& .MuiAccordionSummary-root': {
            minHeight: 'auto',
            padding: '16px 20px',
            backgroundColor: 'white',
            '&.Mui-expanded': {
              minHeight: 'auto',
            },
          },
          '& .MuiAccordionSummary-content': {
            margin: 0,
            '&.Mui-expanded': {
              margin: 0,
            },
          },
          '& .MuiAccordionDetails-root': {
            padding: '0 20px 20px',
            backgroundColor: 'white',
          },
          '&.Mui-disabled': {
            backgroundColor: 'grey.50',
          },
        }}
      >
        <AccordionSummary
          expandIcon={null}
          sx={{
            '& .MuiAccordionSummary-expandIconWrapper': {
              transform: 'none',
            },
          }}
        >
          {actualState === 'noBankAccount' && (
            <NotAvailableBankAccountsTitle
              setOpenCreateAvailableDialog={() =>
                setOpenCreateBankAccount(true)
              }
            />
          )}
          {canShowPayoutPlanTitle() && (
            <PayoutPlanTitle
              actualAmountToDeposit={getAccumulatedAmount() ?? 0}
              totalAmountToDeposit={operationToDeposit ?? 0}
            />
          )}
        </AccordionSummary>

        <AccordionDetails>
          {canShowPayoutPlanDetails() && (
            <PayoutPlanDetails
              selectedPayoutPlan={payoutPlan}
              handleOpenCreateAvailableDialog={() =>
                setOpenCreateBankAccount(true)
              }
              handleOpenEditPayoutPlan={() => setOpenEditPayoutPlan(true)}
              onEditing={onEditing}
            />
          )}
        </AccordionDetails>
      </Accordion>
      <BankAccountDialog
        open={openCreateBankAccount}
        setOpen={setOpenCreateBankAccount}
        businessId={businessIdentifier}
        handleSuccessExit={handleSuccessCreateBankAccount}
      />
      <EditPayoutPlanDialog
        open={openEditPayoutPlan}
        setOpen={setOpenEditPayoutPlan}
        payoutPlan={payoutPlan}
        setPayoutPlan={setPayoutPlan}
        operationToDeposit={operationToDeposit}
        selectableBankAccounts={selectableBankAccounts}
      />
    </>
  );
};

export default PayoutPlanPanel;
